'use client'
import styled from '@emotion/styled'
import { Input } from '@common/components/Input'
import { ButtonContainer } from '@common/components/ButtonContainer'
import { Button } from '@common/components/Button'
import { useForm } from 'react-hook-form'

import { useBookDemoMutation } from '@graphql/generated'
import toast from 'react-hot-toast'
import { ApolloError, gql } from '@apollo/client'
import Head from 'next/head'
import { EmailOptions } from '@common/utils/forms'
import { useRouter, useSearchParams } from 'next/navigation'

interface FormData {
  name: string
  email: string
}

gql`
  mutation BookDemo($name: String!, $email: String!) {
    demoBook(input: { name: $name, email: $email })
  }
`

export default function ClientPage() {
  const [bookDemo] = useBookDemoMutation()
  const query = useSearchParams()
  const step = query.get('step') || ''

  const router = useRouter()
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormData>()

  const handleSubmitDemo = () => {
    const { name, email } = getValues()

    bookDemo({ variables: { name, email } })
      .then(({ data }) => {
        const valid = data?.demoBook ?? false

        if (valid) {
          router.push('/demo?step=success')
        } else {
          toast.error('Ett oväntat fel uppstod')
        }
      })
      .catch((response: ApolloError) => {
        if (!('networkError' in response) || !response.networkError) {
          toast.error('Ett oväntat fel uppstod')
          return
        }

        if (!('statusCode' in response.networkError)) {
          toast.error('Ett oväntat fel uppstod')
          return
        }

        const statusCode = response.networkError.statusCode

        if (statusCode === 429) {
          toast.error('Vi har redan mottagit din bokning')
        } else {
          toast.error('Ett oväntat fel uppstod')
        }
      })
  }

  if (step === 'success') {
    return (
      <DialogContainer
        onSubmit={(e) => {
          e.preventDefault()
          router.replace('/')
        }}
        style={{ textAlign: 'center' }}
      >
        <Dialog>
          <DialogHeader>Tack för din bokning!</DialogHeader>
          <DialogSubtitle>Vi kommer kontakta dig på {getValues('email')}</DialogSubtitle>
          <ButtonContainer style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
            <Button variant="tertiary">Till startisdan</Button>
          </ButtonContainer>
        </Dialog>
      </DialogContainer>
    )
  }

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <title>Boka demo | Booet</title>
        <meta name="description" content="Få en genomgång av tjänsten och se hur den kan passa just din förening." />
      </Head>
      <DialogContainer onSubmit={handleSubmit(handleSubmitDemo)}>
        <Dialog>
          <DialogHeader>Boka demo av Booet</DialogHeader>
          <DialogSubtitle>
            Få en genomgång av tjänsten och se hur den kan passa just din förening. Vi återkopplar oftast inom ett dygn.
          </DialogSubtitle>
          <div style={{ marginTop: '1rem' }} />
          <Input
            autoFocus
            error={errors.name?.message}
            placeholder="Brf Booet"
            type="text"
            label="Föreningens namn"
            {...register('name', {
              required: 'Ange föreningens namn',
              minLength: {
                value: 3,
                message: 'Ogiltigt namn',
              },
              maxLength: {
                value: 50,
                message: 'Ogiltigt namn',
              },
            })}
          />
          <div style={{ marginTop: '1rem' }} />
          <Input
            error={errors.email?.message}
            autoComplete="on"
            placeholder="mail@gmail.com"
            type="email"
            label="E-mail"
            {...register('email', EmailOptions)}
          />
          <div style={{ marginTop: '2rem' }} />
          <ButtonContainer>
            <Button type="submit">Skicka</Button>
            <Button
              variant="secondary"
              type="submit"
              onClick={(e) => {
                e.preventDefault()
                router.back()
              }}
            >
              Avbryt
            </Button>
          </ButtonContainer>
        </Dialog>
      </DialogContainer>
    </>
  )
}

const DialogContainer = styled.form`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 90vh;
`

const Dialog = styled.div`
  margin-top: -2rem;
  width: 30rem;
  padding: 2rem;
`

const DialogHeader = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.onBackground};
`

const DialogSubtitle = styled.p`
  margin-top: 0.25rem;
  font-size: 1rem;
  color: ${({ theme }) => theme.onBackgroundSemiLight};
`
